<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { RouteRecordRaw, useRoute } from "vue-router";
import { HomePageType } from "~/api";
import SideMenu from "~/components/SideMenu.vue";
import TopBar from "~/components/TopBar.vue";
import getMainRouter, {
  applicationRouteMap,
  workflowUrlPrefix,
} from "~/router/mainRouter";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { getModeDisplay } from "~/utils/miscUtils";
import { getEnvConfig } from "~/services/appConfig";

const router = getMainRouter();
const myInfoStore = useMyInfoStore();
const routes = applicationRouteMap.Compliance.children;
const route = useRoute();
// handle special homepage design types

const homePageType = ref(HomePageType.DEFAULT);

const chooseHomePage = () => {
  if (homePageType.value === HomePageType.WORKFLOWS) {
    // Redirect to the Workflows view and the current path is not already a workflow path.

    const currentPath = route.path;
    const wfPrefix = "/" + workflowUrlPrefix;
    const otherAllowedPathPrefixes = ["/pdf"];

    if (
      otherAllowedPathPrefixes.some((prefix) => currentPath.startsWith(prefix))
    ) {
      // we may show this page
    } else if (myInfoStore.isGuest && !currentPath.startsWith(wfPrefix)) {
      // if this person is a guest and has Workflow home page type, we only show the workflow page
      const wfGuid =
        myInfoStore.currentSysOrg?.entityDto?.homePageWorkflowDefinitionGuid;
      // console.log("Redirecting to workflow page:", wfPrefix + wfGuid);
      router.push({
        path: wfPrefix + wfGuid,
      });
    } else {
      // not a guest, show others???
    }
  }
};

const setupRoutes = computed(() => Object.values(routes.Setup.children));

const manageRoutes = computed(() =>
  Object.values(routes.Manage.children).map((r: RouteRecordRaw) => {
    if (r.children) {
      r.children = Object.values(r.children);
    }
    return r;
  }),
);

// Show the side menu if the user is logged in and not viewing a PDF
// and the home page type is default
const showSideMenu = computed(() => {
  return (
    myInfoStore.userInfo &&
    !isPdfViewerRoute.value &&
    homePageType.value === HomePageType.DEFAULT
  );
});

const showTopHeader = computed(() => {
  return !isPdfViewerRoute.value;
});

/**
 * Checks the current route and returns the corresponding message for the top bar.
 */
const getTopBarMessage = computed<string>(() => {
  const currentRoutePathStart = router.currentRoute.value.path.split("/")[1];

  if (setupRoutes.value.find((r) => r.path === currentRoutePathStart)) {
    return "Setup";
  }

  if (manageRoutes.value.find((r) => r.path === currentRoutePathStart)) {
    return "Manage";
  }

  return "";
});

// Check if the current route is the PDF viewer
const isPdfViewerRoute = computed(() => {
  return route.path.includes("/pdf/");
});
const showWarning = getEnvConfig().env !== "live";

onMounted(async () => {
  // add class to the body when the page is loaded
  document.body.classList.add("compliance");

  await myInfoStore.load();
  homePageType.value =
    myInfoStore.currentSysOrg?.entityDto?.homePage ?? HomePageType.DEFAULT;

  chooseHomePage();
});

onUnmounted(() => {
  document.body.classList.remove("compliance");
});
</script>
<template>
  <el-affix
    v-if="getTopBarMessage"
    position="top"
    :offset="60"
    class="info-bar"
  >
    <div class="info">{{ getTopBarMessage }}</div>
  </el-affix>
  <el-container class="mainContainer compliance">
    <div v-if="showWarning" class="warningContainer">
      <div class="warning">{{ getModeDisplay() }}</div>
    </div>

    <el-header v-if="showTopHeader" class="border homeHeader">
      <TopBar />
    </el-header>

    <el-container
      class="subContainer"
      :style="{ 'margin-top': showTopHeader ? '0' : '' }"
    >
      <SideMenu v-if="showSideMenu" />
      <el-main class="border homeMain" :class="{ 'full-width': showSideMenu }">
        <RouterView />
      </el-main>
    </el-container>
  </el-container>
</template>
<style lang="less">
body.compliance {
  --el-font-size-base: 1rem; // match host site size
  --el-checkbox-font-size: 1rem; // used?
  font-family: "Poppins", sans-serif;
  font-size: var(--el-font-size-base);

  @primary-color: #324632;
  --el-color-primary: @primary-color;
  --el-color-primary-light-3: lighten(@primary-color, 10%);
  --el-color-primary-light-5: lighten(@primary-color, 50%);
  --el-color-primary-light-7: lighten(@primary-color, 55%);
  --el-color-primary-light-8: lighten(@primary-color, 60%);
  --el-color-primary-light-9: lighten(@primary-color, 75%);
  --el-color-primary-dark-2: darken(@primary-color, 10%);
  --el-color-primary-dark-3: darken(@primary-color, 15%);

  --background-color: #ffffff;
  --background-color-light: #ffffff;
  --el-color-primary-light-9: #ffffff;

  --subheading-color: #324632;

  @info-color: #646464;
  --el-color-info: @info-color;
  --el-color-info-light-3: lighten(@info-color, 30%);
  --el-color-info-light-5: lighten(@info-color, 40%);
  --el-color-info-light-7: lighten(@info-color, 45%);
  --el-color-info-light-8: lighten(@info-color, 50%);
  --el-color-info-light-9: lighten(@info-color, 55%);
  --el-color-info-dark-2: darken(@info-color, 20%);

  /* adjusted to meet AAA contrast ratio with primary-light-9 */
  --el-color-danger: #a10000;
  /* --el-color-primary: #9f806b; */
  --silveraColorPrimary: #536e82;
  --el-text-color-primary: #536e82;
  --el-text-color-regular: #606266;
  --el-text-color-secondary: #909399;

  // so the checkbox text is black
  --el-text-color-regular: #2a2829;

  color: var(--el-text-color-regular);

  .el-header {
    --el-header-height: 60px;
  }
  --el-header-height: 60px;

  button {
    font-family: "Poppins", sans-serif;
  }

  .FileUploadComponent {
    .el-upload-list__item-name {
      .el-icon {
        color: var(--el-color-primary);
      }
    }
  }

  .el-message-box {
    .el-message-box__title {
      font-size: 1.5rem;
    }
    .el-message-box__message p {
      font-size: 1.3rem;
      line-height: normal;
      margin: 0 1em 0 0.5em;
    }
    .el-button {
      font-size: 1rem;
    }
  }

  // need both so the icon and text are the same size
  .el-message,
  .el-message__content {
    font-size: 1.3rem;
  }
}

.el-affix.info-bar {
  height: 0;
  width: 100% !important;
}
.info-bar {
  .info {
    text-align: center;
    background-color: #ffd04b;
    width: 100%;
    font-size: 0.8em;
    color: #5b5b5b;
    font-weight: 600;
  }
}

.compliance {
  &.mainContainer {
    .warningContainer {
      width: 100%;
      z-index: 10;
      color: var(--el-color-primary-light-8);
      background-color: var(--subheading-color);
      .warning {
        margin: auto;
      }
    }
    .subContainer {
      display: flex;
      flex-direction: row; // Changed from column to row to align side by side
      min-height: 0; // Allow the container to shrink to fit its content

      .links {
        display: flex;
        justify-content: space-evenly;
        padding: 0.5em;
        border-bottom: 1px solid black;
      }
    }

    .homeHeader {
      background-color: var(--background-color);
      box-shadow: 0px 0px 6px 4px hsl(0deg 0% 0% / 15%);
      z-index: 101;
    }
    // This is the height - header height necessary for scrolling
    .homeMain {
      min-height: 1px;

      &.full-width {
        width: 100%;
        padding: 20px;
        // height: 100vh; /* Full viewport height when in PDF viewer mode */
      }
    }
  }

  .right {
    margin-top: 0.25em;
    align-items: end;
    display: flex;
    flex-direction: column-reverse;
  }

  .environmentContainer {
    position: absolute;
    left: 45%;
    padding: 0 1em;
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    z-index: 102;
    color: white;
    background-color: var(--subheading-color);
  }
}
</style>
